import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  FileText, 
  Plus, 
  Download, 
  Trash2, 
  Search,
  ArrowLeft,
  Upload,
  Folder,
  File,
  Calendar,
  X,
  Edit3,
  RefreshCw,
  Filter,
  ChevronLeft,
  ChevronRight,
  Image,
  FileArchive,
  Eye,
  EyeOff,
  File as FileIcon
} from 'lucide-react'

const AdminFiles = () => {
  const [files, setFiles] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedSourceModule, setSelectedSourceModule] = useState('')
  const [selectedType, setSelectedType] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalFiles, setTotalFiles] = useState(0)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [editFile, setEditFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadCategoryId, setUploadCategoryId] = useState('')
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadDisplayName, setUploadDisplayName] = useState('')
  const fileInputRef = useRef(null)
  const navigate = useNavigate()

  const PAGE_SIZE = 20

  useEffect(() => {
    const token = localStorage.getItem('token')
    const userData = localStorage.getItem('user')
    if (!token || !userData) {
      navigate('/admin/login')
      return
    }
    loadCategories()
    loadFiles()
  }, [navigate])

  useEffect(() => {
    setCurrentPage(1)
    loadFiles(1)
  }, [searchTerm, selectedCategory, selectedType, selectedSourceModule])

  const loadCategories = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/files/categories/list', {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setCategories(data.data || [])
        }
      }
    } catch (error) {
      console.error('加载分类失败:', error)
    }
  }

  const loadFiles = async (page = currentPage) => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      const params = new URLSearchParams()
      params.append('page', page.toString())
      params.append('limit', PAGE_SIZE.toString())
      if (searchTerm) params.append('search', searchTerm)
      if (selectedCategory) params.append('categoryId', selectedCategory)
      if (selectedType) params.append('fileType', selectedType)
      if (selectedSourceModule) params.append('sourceModule', selectedSourceModule)

      const response = await fetch(`/api/files?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setFiles(data.data.files || [])
          setTotalFiles(data.data.total || 0)
          setTotalPages(data.data.totalPages || 1)
          setCurrentPage(data.data.page || 1)
        }
      }
    } catch (error) {
      console.error('加载文件列表失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUploadClick = () => {
    setUploadFiles([])
    setUploadCategoryId('')
    setUploadDescription('')
    setUploadDisplayName('')
    setUploadProgress(0)
    setShowUploadModal(true)
  }

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files)
    setUploadFiles(prev => [...prev, ...selectedFiles])
  }

  const removeUploadFile = (index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleUpload = async () => {
    if (uploadFiles.length === 0) {
      alert('请选择要上传的文件')
      return
    }

    setUploading(true)
    setUploadProgress(0)

    try {
      const token = localStorage.getItem('token')
      let successCount = 0
      let failCount = 0

      for (let i = 0; i < uploadFiles.length; i++) {
        const formData = new FormData()
        formData.append('file', uploadFiles[i])
        if (uploadCategoryId) formData.append('categoryId', uploadCategoryId)
        if (uploadDescription) formData.append('description', uploadDescription)
        if (uploadDisplayName) formData.append('displayName', uploadDisplayName)

        try {
          const response = await fetch('/api/files/upload', {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData
          })

          if (response.ok) {
            const data = await response.json()
            if (data.success) {
              successCount++
            } else {
              failCount++
            }
          } else {
            failCount++
          }
        } catch (err) {
          failCount++
        }

        setUploadProgress(Math.round(((i + 1) / uploadFiles.length) * 100))
      }

      alert(`上传完成：成功 ${successCount} 个，失败 ${failCount} 个`)
      setShowUploadModal(false)
      setUploading(false)
      loadFiles(1)
    } catch (error) {
      console.error('上传失败:', error)
      alert('上传失败: ' + error.message)
      setUploading(false)
    }
  }

  const handleEdit = (file) => {
    setEditFile({ ...file })
    setShowEditModal(true)
  }

  const handleSaveEdit = async () => {
    if (!editFile) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/files/${editFile.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          displayName: editFile.displayName || null,
          description: editFile.description,
          categoryId: editFile.categoryId,
          requiresLogin: editFile.requiresLogin,
          isActive: editFile.isActive
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success) {
          setShowEditModal(false)
          setEditFile(null)
          loadFiles()
        }
      }
    } catch (error) {
      console.error('更新文件失败:', error)
      alert('更新文件失败')
    }
  }

  const handleDownload = async (file) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/files/${file.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = file.name
        document.body.appendChild(a)
        a.click()
        window.URL.revokeObjectURL(url)
        document.body.removeChild(a)
        loadFiles() // 刷新下载次数
      }
    } catch (error) {
      console.error('下载文件失败:', error)
    }
  }

  const handleDelete = async (fileId) => {
    if (!window.confirm('确定要删除这个文件吗？此操作不可恢复！')) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        loadFiles()
      }
    } catch (error) {
      console.error('删除文件失败:', error)
    }
  }

  const handleToggleActive = async (file) => {
    const action = file.isActive !== false ? '隐藏' : '显示'
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/files/${file.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isActive: file.isActive === false ? true : false,
          displayName: file.displayName || null,
          description: file.description,
          categoryId: file.categoryId
        })
      })

      if (response.ok) {
        loadFiles()
      }
    } catch (error) {
      console.error(`${action}文件失败:`, error)
      alert(`${action}文件失败`)
    }
  }

  const getFileIcon = (fileType) => {
    if (fileType?.includes('image')) {
      return <Image size={20} className="text-blue-500" />
    } else if (fileType?.includes('archive') || fileType?.includes('zip')) {
      return <FileArchive size={20} className="text-yellow-600" />
    } else if (fileType?.includes('document')) {
      return <FileText size={20} className="text-green-600" />
    } else if (fileType?.includes('video')) {
      return <FileText size={20} className="text-purple-500" />
    } else {
      return <FileIcon size={20} className="text-gray-500" />
    }
  }

  const getFileIconEmoji = (fileType) => {
    if (fileType?.includes('image')) return '🖼️'
    if (fileType?.includes('pdf')) return '📄'
    if (fileType?.includes('word') || fileType?.includes('document')) return '📝'
    if (fileType?.includes('excel') || fileType?.includes('spreadsheet')) return '📊'
    if (fileType?.includes('zip') || fileType?.includes('rar') || fileType?.includes('archive')) return '📦'
    if (fileType?.includes('video')) return '🎬'
    return '📎'
  }

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('zh-CN')
  }

  // 统计信息
  const stats = {
    total: totalFiles,
    totalSize: files.reduce((sum, f) => sum + (f.fileSize || 0), 0),
    imageCount: files.filter(f => f.fileType?.includes('image')).length,
    docCount: files.filter(f => f.fileType?.includes('document')).length
  }

  if (loading && files.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部导航栏 */}
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <ArrowLeft size={16} />
                <span>返回仪表板</span>
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                技术支持{selectedSourceModule === 'website_files' ? ' - 网站文件' : ''}
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => loadFiles(1)}
                className="text-gray-500 hover:text-gray-700 p-2 rounded-md hover:bg-gray-100"
                title="刷新"
              >
                <RefreshCw size={18} />
              </button>
              <button
                onClick={handleUploadClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-2"
              >
                <Upload size={16} />
                <span>上传文件</span>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* 主要内容区域 */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* 统计信息 */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">文件总数</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <FileText className="text-blue-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">总大小</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatFileSize(stats.totalSize)}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <Folder className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">图片文件</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.imageCount}</p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <span className="text-2xl">🖼️</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">文档文件</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.docCount}</p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <File className="text-orange-600" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* 搜索和筛选栏 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="搜索文件名称或描述..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-3">
                <Filter size={18} className="text-gray-400" />
                <select
                  value={selectedSourceModule}
                  onChange={(e) => setSelectedSourceModule(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">全部来源</option>
                  <option value="file_management">技术支持文件</option>
                  <option value="website_files">网站文件</option>
                </select>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">全部分类</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
                <select
                  value={selectedType}
                  onChange={(e) => setSelectedType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">全部类型</option>
                  <option value="image">图片</option>
                  <option value="document">文档</option>
                  <option value="video">视频</option>
                  <option value="archive">压缩包</option>
                  <option value="other">其他</option>
                </select>
              </div>
            </div>
          </div>

          {/* 文件列表 */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                文件列表 ({totalFiles})
              </h2>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      文件信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      分类
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      来源
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      大小
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      下载
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      上传时间
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {files.length > 0 ? (
                    files.map((file) => (
                      <tr key={file.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                              <span className="text-lg">{getFileIconEmoji(file.fileType)}</span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {file.displayName || file.name}
                              </div>
                              <div className="text-sm text-gray-500">
                                {file.description || '暂无描述'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 py-1 bg-gray-100 rounded-full text-xs">
                            {file.category?.name || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                            {file.sourceModule === 'file_management' ? '技术支持' :
                             file.sourceModule === 'product' ? '产品' :
                             file.sourceModule === 'carousel' ? '轮播图' :
                             file.sourceModule === 'logo' ? 'LOGO' :
                             file.sourceModule === 'company' ? '公司简介' :
                             file.sourceModule || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {file.sourceModule === 'file_management' ? (
                            <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs" title="技术支持模块文件">
                              技术支持
                            </span>
                          ) : file.referenced ? (
                            <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs" title={file.referenceDetails || '已关联'}>
                              使用中
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-red-50 text-red-600 rounded-full text-xs" title={file.referenceDetails || '未关联任何业务数据，可以安全删除'}>
                              可清理
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatFileSize(file.fileSize)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {file.downloadCount || 0} 次
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center space-x-1">
                            <Calendar size={14} />
                            <span>{formatDate(file.createdAt)}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => handleDownload(file)}
                              className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                              title="下载"
                            >
                              <Download size={16} />
                            </button>
                            <button 
                              onClick={() => handleEdit(file)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                              title="编辑"
                            >
                              <Edit3 size={16} />
                            </button>
                            <button 
                              onClick={() => handleToggleActive(file)}
                              className={file.isActive !== false 
                                ? 'text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-100' 
                                : 'text-orange-500 hover:text-orange-700 p-1 rounded hover:bg-orange-50'}
                              title={file.isActive !== false ? '隐藏（前台不显示）' : '显示（前台可见）'}
                            >
                              {file.isActive !== false ? <EyeOff size={16} /> : <Eye size={16} />}
                            </button>
                            <button 
                              onClick={() => handleDelete(file.id)}
                              className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                              title="删除"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center">
                        <FileText className="mx-auto text-gray-400" size={48} />
                        <p className="mt-4 text-gray-500">暂无技术支持资源</p>
                        <p className="text-sm text-gray-400 mb-6">上传技术支持文件以供用户下载</p>
                        <button 
                          onClick={handleUploadClick}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md font-medium flex items-center space-x-2 mx-auto"
                        >
                          <Upload size={16} />
                          <span>上传第一个文件</span>
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  共 {totalFiles} 个文件，第 {currentPage}/{totalPages} 页
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => loadFiles(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (currentPage <= 3) {
                      pageNum = i + 1
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = currentPage - 2 + i
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => loadFiles(pageNum)}
                        className={`px-3 py-1 border rounded-md text-sm ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  <button
                    onClick={() => loadFiles(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 上传弹窗 */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">上传文件</h3>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* 分类选择 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  文件分类
                </label>
                <select
                  value={uploadCategoryId}
                  onChange={(e) => setUploadCategoryId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">选择分类（可选）</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* 自定义显示名称 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  显示名称
                  <span className="text-gray-400 font-normal ml-1">（可选，默认为原文件名）</span>
                </label>
                <input
                  type="text"
                  value={uploadDisplayName}
                  onChange={(e) => setUploadDisplayName(e.target.value)}
                  placeholder="输入自定义显示名称..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* 描述 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  文件描述（可选）
                </label>
                <input
                  type="text"
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  placeholder="输入文件描述..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* 文件选择区域 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  选择文件
                </label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <Upload className="mx-auto text-gray-400" size={40} />
                  <p className="mt-2 text-sm text-gray-600">
                    点击选择文件或拖拽文件到此处
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    支持图片、文档、压缩包等，单文件最大 50MB
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>

              {/* 已选文件列表 */}
              {uploadFiles.length > 0 && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">
                    已选择 {uploadFiles.length} 个文件
                  </p>
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {uploadFiles.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm">
                        <div className="flex items-center space-x-2 truncate flex-1">
                          <span>{getFileIconEmoji(file.type)}</span>
                          <span className="truncate">{file.name}</span>
                          <span className="text-gray-400 text-xs">{formatFileSize(file.size)}</span>
                        </div>
                        <button
                          onClick={() => removeUploadFile(index)}
                          className="text-red-400 hover:text-red-600 ml-2"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 上传进度 */}
              {uploading && (
                <div>
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>上传中...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    ></div>
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowUploadModal(false)}
                disabled={uploading}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                取消
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading || uploadFiles.length === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Upload size={16} />
                <span>{uploading ? '上传中...' : '开始上传'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 编辑弹窗 */}
      {showEditModal && editFile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">编辑文件信息</h3>
              <button
                onClick={() => { setShowEditModal(false); setEditFile(null) }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">显示名称</label>
                <input
                  type="text"
                  value={editFile.displayName || ''}
                  onChange={(e) => setEditFile({ ...editFile, displayName: e.target.value || null })}
                  placeholder="留空则显示原始文件名"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">原始文件名称</label>
                <input
                  type="text"
                  value={editFile.name}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">文件描述</label>
                <textarea
                  value={editFile.description || ''}
                  onChange={(e) => setEditFile({ ...editFile, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">文件分类</label>
                <select
                  value={editFile.categoryId || ''}
                  onChange={(e) => setEditFile({ ...editFile, categoryId: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">无分类</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editFile.requiresLogin || false}
                    onChange={(e) => setEditFile({ ...editFile, requiresLogin: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">需要登录下载</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={editFile.isActive !== false}
                    onChange={(e) => setEditFile({ ...editFile, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">启用</span>
                </label>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-500 space-y-1">
                <p>类型: {editFile.fileType} | MIME: {editFile.mimeType}</p>
                <p>大小: {formatFileSize(editFile.fileSize)}</p>
                <p>来源模块: {editFile.sourceModule || '-'}</p>
                <p>下载次数: {editFile.downloadCount || 0}</p>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => { setShowEditModal(false); setEditFile(null) }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                取消
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
              >
                保存修改
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminFiles
